import { DashboardMarket, DashboardMarketAsset, LINKS } from "@silo/types";
import { ReactNode } from "react";
import { getBorrowableAssets, getNativeRewardsSymbol } from "./utils";
import { Array, Option, pipe } from "effect";
import { disjoin } from "@silo/utils";
import { StaticDescriptionContent } from "../description";
import { LendingLinkContext } from "./lending-link-context";
import { AssetContainer, DetailsContainer } from "./components";
import { PillContainer, PositionTypePill, TokenPill } from "./components/pills";
import {
  BalancePair,
  LiquidationPricePair,
  List,
  PointsPair,
} from "./components/list";
import { AprDisplayV2 } from "../apr-display-v2";
import { ExternalLink } from "./components/external-link";
import { ActionContainer } from "./components/actions";

interface DebtAssetProps {
  className?: string;
  market: DashboardMarket;
  asset: DashboardMarketAsset;
  tokenLogo: ReactNode;
  divider?: boolean;
  borrowMoreLink: ReactNode;
  repayLink: ReactNode;
}

export function DebtAsset({
  className,
  market,
  asset,
  tokenLogo,
  divider,
  borrowMoreLink,
  repayLink,
}: DebtAssetProps): JSX.Element {
  const {
    debtBalance,
    debtBalanceUsd,
    decimals,
    protocolKey,
    debtPoints,
    debtLiquidationPriceUsd,
    priceNative,
  } = asset;

  const nativeRewardsSymbol = getNativeRewardsSymbol(protocolKey);

  const isPriceUnavailable = priceNative === 0n;

  return (
    <AssetContainer>
      <DetailsContainer className={className}>
        <PillContainer>
          <TokenPill asset={asset} tokenLogo={tokenLogo} />

          <PositionTypePill>Borrow</PositionTypePill>
        </PillContainer>

        <List divider={divider}>
          <BalancePair
            label="Borrowed"
            balance={debtBalance}
            balanceUsd={isPriceUnavailable ? null : debtBalanceUsd}
            decimals={decimals}
          />

          <AprDisplayV2.PositionDebt
            asset={asset}
            nativeRewardsSymbol={nativeRewardsSymbol}
          />

          {Array.isNonEmptyReadonlyArray(debtPoints) && (
            <PointsPair points={debtPoints} />
          )}

          <LiquidationPricePair
            liquidationPriceUsd={debtLiquidationPriceUsd}
            description={<LiquidationPriceDescription />}
          />
        </List>
      </DetailsContainer>

      {divider && <div className="h-px bg-shale-700" />}

      <ActionContainer market={market} asset={asset}>
        {borrowMoreLink}

        {repayLink}
      </ActionContainer>
    </AssetContainer>
  );
}

export function BorrowingDisabled(): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-5">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-medium text-white">
          Borrowing is currently disabled.
        </h2>
        <p className="text-shale-200">
          You can{"'"}t open a borrow position when you have deposited all of
          the silo{"'"}s assets.{" "}
        </p>
      </div>

      <div>
        <ExternalLink href={LINKS.SILOPEDIA.DISABLED_BORROWING} />
      </div>
    </div>
  );
}

interface NoDebtProps {
  market: DashboardMarket;
  borrowLink: ReactNode;
}

export function NoDebt({ market, borrowLink }: NoDebtProps): JSX.Element {
  const borrowableAssets = getBorrowableAssets(market);
  const asset = pipe(borrowableAssets, Array.head, Option.getOrUndefined);

  const symbols = pipe(
    borrowableAssets,
    Array.map((asset) => asset.symbol),
    disjoin
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-medium text-white">
          You don{"'"}t have any positions yet.
        </h2>
        <p className="text-shale-200">
          Borrow some {symbols} to see your position here.
        </p>
      </div>

      <ActionContainer market={market} asset={asset}>
        {borrowLink}
      </ActionContainer>
    </div>
  );
}

function LiquidationPriceDescription(): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-5">
      <p>
        This is the simulated asset price at which the borrow position becomes
        insolvent (HF goes to 0%).
      </p>

      <p>
        If you have borrowed two assets, the liquidation price for each asset is
        weighted to reflect its impact on the position.
      </p>

      <div>
        <ExternalLink href={LINKS.SILOPEDIA.LIQUIDATION_PRICE} />
      </div>
    </div>
  );
}
